import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useMediaQuery } from 'react-responsive';

import { getCatalogItems } from 'api/jalapeno';
import Button from 'honeydew/Button';
import useInAssortmentPreview from 'hooks/useInAssortmentPreview';
import useTailwindTheme from 'hooks/useTailwindTheme';
import HowItWorks from 'pages/signup/leadCapture/HowItWorks';
import PricingDisclaimer from 'shared/PricingDisclaimer/PricingDisclaimer';
import { trackEvent } from 'utils/analyticsUtils';

import AssortmentAisleCard from './AssortmentAisleCard';
import assortmentPreviewModuleData from './assortmentPreviewModuleData';
import AssortmentProductCard from './AssortmentProductCard';

function AssortmentPreviewModule() {
    const isMango = window.location.pathname.includes('mango');
    const previouslySelectedAisle = parseInt(localStorage.getItem('assortmentModuleAisleId'), 10);
    const { inAssortmentPreviewPhaseTwo } = useInAssortmentPreview();
    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });

    const [currentAisle, setCurrentAisle] = useState(previouslySelectedAisle || 1);
    const [aisles, setAisles] = useState([]);

    const getItems = async () => {
        const externalSkus = [];
        assortmentPreviewModuleData?.forEach((aisle) => {
            externalSkus.push(...aisle.externalSkus.slice(0, 6));
        });

        const response = await getCatalogItems({ externalSkus });

        response?.data?.items?.forEach((item) => {
            assortmentPreviewModuleData?.forEach((aisle) => {
                const newAisle = aisle;
                const itemIndex = aisle?.items.findIndex((backup) => backup.externalSku === item.externalSku);
                if (itemIndex > -1) {
                    newAisle.items[itemIndex] = item;
                }
                return newAisle;
            });
        });

        setAisles(assortmentPreviewModuleData);
    };

    const onPreviewCtaClick = () => {
        trackEvent('grocery preview module cta clicked', {
            location: isMango ? 'home page' : 'lead capture page',
        });
    };

    useEffect(() => {
        getItems();
    }, []);

    return (
        <div className={clsx({ 'h-screen w-screen': isMango && mobile })}>
            {!isMango && (
                <div className="bg-brand-secondary">
                    <HowItWorks isAssortmentPreview />
                </div>
            )}
            <div className="bg-malt-off-white" id={!isMango ? 'preview-header' : ''}>
                <div className="flex flex-col items-center justify-center px-20 pt-20">
                    <h1
                        className={clsx('md:text-center', {
                            'text-heading-md': isMango,
                            'text-body-lg font-grotesk-bold': !isMango,
                        })}
                    >
                        What groceries do we carry?
                    </h1>
                    <p className="text-body-sm mx-auto mt-20 max-w-[670px] md:text-center">
                        We never know exactly what food will need rescuing, so our assortment is always changing. But
                        one thing you can count on is that we’ll have a wide selection of high-quality, more sustainable
                        groceries.
                    </p>
                    <div
                        className={clsx(
                            'relative flex gap-10 overflow-x-auto p-20 pb-5 pt-20 md:grid md:max-w-[710px] md:grid-cols-3 min-[1280px]:mx-auto min-[1280px]:flex min-[1280px]:max-w-none min-[1280px]:justify-center',
                            { 'w-screen': mobile }
                        )}
                    >
                        {aisles?.map((aisle) => (
                            <AssortmentAisleCard
                                aisle={aisle}
                                key={aisle.id}
                                currentAisle={currentAisle}
                                setCurrentAisle={setCurrentAisle}
                            />
                        ))}
                    </div>
                    <div
                        className={clsx(
                            'relative grid min-h-[260px] max-w-[1000px] grid-cols-2 px-10 pt-10 sm:px-20 sm:pt-20 min-[540px]:grid-cols-3 md:max-w-[570px] min-[1025px]:max-w-[1100px] min-[1025px]:grid-cols-6',
                            { 'w-screen': isMango && mobile }
                        )}
                    >
                        {aisles[currentAisle - 1]?.items?.map((item) => (
                            <AssortmentProductCard item={item} key={item.id} aisle={aisles[currentAisle - 1].name} />
                        ))}
                    </div>
                </div>
                {inAssortmentPreviewPhaseTwo && (
                    <div className="mx-auto max-w-[300px] pb-20">
                        <Button
                            variant="secondary"
                            as={isMango ? 'a' : 'link'}
                            to="/grocery-and-produce-delivery-sneak-peek"
                            onClick={onPreviewCtaClick}
                            target={isMango ? '_parent' : null}
                        >
                            See Our Sample Groceries
                        </Button>
                    </div>
                )}
                {!isMango && (
                    <div className="mx-auto mb-20 max-w-[1100px] px-20 text-center md:pr-20 md:text-right">
                        <PricingDisclaimer />
                    </div>
                )}
            </div>
        </div>
    );
}

export default AssortmentPreviewModule;
